import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Slide,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Medication as MedicationIcon,
  Scale as ScaleIcon,
  Schedule as ScheduleIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import { ValidationResult } from '../utils/medicalValidators';
import { sourceManager } from '../utils/medicalSources';
import { historyManager } from '../utils/history';
import { favoritesManager } from '../utils/favorites';
import { dataValidationManager } from '../utils/dataValidation';
import { auditLogger } from '../utils/auditLogger';
import { useAuth } from '../contexts/AuthContext';
import MedicalInfo from '../components/MedicalInfo';

interface CalculationResult {
  dose: number;
  unit: string;
  frequency: string;
  duration: string;
  notes: string[];
  maxDailyDose?: number;
  ageGroup?: string;
  type?: 'medication' | 'rehydration';
}

interface RehydrationResult {
  type: 'rehydration';
  totalVolume: number;
  volumeUnit: string;
  hourlyRate: number;
  rateUnit: string;
  duration: string;
  solution: string;
  administration: string;
  notes: string[];
  ageGroup?: string;
}

const Calculator: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [weight, setWeight] = useState('');
  const [age, setAge] = useState('');
  const [medication, setMedication] = useState('');
  const [calculationType, setCalculationType] = useState<'medication' | 'rehydration'>('medication');
  const [result, setResult] = useState<CalculationResult | RehydrationResult | null>(null);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [validationResult] = useState<ValidationResult | null>(null);
  const { user } = useAuth();

  const medications = [
    { value: 'paracetamol', label: 'Парацетамол', category: 'Жаропонижающие', minAge: 0, maxAge: 18 },
    { value: 'ibuprofen', label: 'Ибупрофен', category: 'НПВС', minAge: 0.25, maxAge: 18 },
    { value: 'amoxicillin', label: 'Амоксициллин', category: 'Антибиотики', minAge: 0, maxAge: 18 },
    { value: 'azithromycin', label: 'Азитромицин', category: 'Антибиотики', minAge: 0.5, maxAge: 18 },
    { value: 'ceftriaxone', label: 'Цефтриаксон', category: 'Антибиотики', minAge: 0, maxAge: 18 },
    { value: 'clarithromycin', label: 'Кларитромицин', category: 'Антибиотики', minAge: 0, maxAge: 18 },
    { value: 'cefuroxime', label: 'Цефуроксим', category: 'Антибиотики', minAge: 0, maxAge: 18 },
    { value: 'cefixime', label: 'Цефиксим', category: 'Антибиотики', minAge: 0, maxAge: 18 },
    { value: 'prednisolone', label: 'Преднизолон', category: 'Гормоны', minAge: 0, maxAge: 18 },
    { value: 'salbutamol', label: 'Сальбутамол', category: 'Бронхолитики', minAge: 0, maxAge: 18 },
    { value: 'furosemide', label: 'Фуросемид', category: 'Диуретики', minAge: 0, maxAge: 18 },
    { value: 'cetirizine', label: 'Цетиризин', category: 'Антигистаминные', minAge: 0.5, maxAge: 18 },
    { value: 'levocetirizine', label: 'Левоцетиризин', category: 'Антигистаминные', minAge: 0.5, maxAge: 18 },
    { value: 'omeprazole', label: 'Омепразол', category: 'Ингибиторы протонной помпы', minAge: 1, maxAge: 18 },
    { value: 'loperamide', label: 'Лоперамид', category: 'Противодиарейные', minAge: 2, maxAge: 18 },
    { value: 'domperidone', label: 'Домперидон', category: 'Прокинетики', minAge: 0, maxAge: 18 },
    { value: 'diphenhydramine', label: 'Дифенгидрамин', category: 'Антигистаминные', minAge: 1, maxAge: 18 },
  ];


  const steps = ['Ввод данных', 'Выбор препарата', 'Результат'];



  // Функция расчета регидратационной терапии
  const calculateRehydration = (): RehydrationResult => {
    const weightNum = parseFloat(weight);
    const ageNum = parseFloat(age) || 0;
    
    // Определение возрастной группы
    let ageGroup = '';
    if (ageNum < 1) ageGroup = 'Грудной возраст (до 1 года)';
    else if (ageNum < 3) ageGroup = 'Ранний возраст (1-3 года)';
    else if (ageNum < 7) ageGroup = 'Дошкольный возраст (3-7 лет)';
    else if (ageNum < 12) ageGroup = 'Младший школьный возраст (7-12 лет)';
    else ageGroup = 'Подростковый возраст (12+ лет)';

    // Расчет общего объема жидкости по весу
    let totalVolume: number;
    let hourlyRate: number;
    let duration: string;
    let solution: string;
    let administration: string;
    const notes: string[] = [];

    if (ageNum < 1) {
      // Грудной возраст: 150-200 мл/кг/сутки
      totalVolume = weightNum * 175; // среднее значение
      hourlyRate = totalVolume / 24;
      duration = '24 часа';
      solution = 'Регидрон, Хумана Электролит, или аналогичные растворы';
      administration = 'По 1-2 чайные ложки каждые 5-10 минут';
      notes.push('При рвоте - пауза 10-15 минут, затем продолжить');
      notes.push('При отказе от раствора - грудное молоко/смесь');
    } else if (ageNum < 3) {
      // Ранний возраст: 100-150 мл/кг/сутки
      totalVolume = weightNum * 125;
      hourlyRate = totalVolume / 24;
      duration = '24 часа';
      solution = 'Регидрон, Хумана Электролит, или аналогичные растворы';
      administration = 'По 1-2 столовые ложки каждые 5-10 минут';
      notes.push('Можно давать из чашки или бутылочки');
      notes.push('При рвоте - пауза 10-15 минут');
    } else if (ageNum < 7) {
      // Дошкольный возраст: 80-120 мл/кг/сутки
      totalVolume = weightNum * 100;
      hourlyRate = totalVolume / 24;
      duration = '24 часа';
      solution = 'Регидрон, Хумана Электролит, или аналогичные растворы';
      administration = 'По 50-100 мл каждые 15-30 минут';
      notes.push('Можно давать из чашки или через трубочку');
      notes.push('При рвоте - пауза 15-30 минут');
    } else {
      // Школьный и подростковый возраст: 60-100 мл/кг/сутки
      totalVolume = weightNum * 80;
      hourlyRate = totalVolume / 24;
      duration = '24 часа';
      solution = 'Регидрон, Хумана Электролит, или аналогичные растворы';
      administration = 'По 100-200 мл каждые 15-30 минут';
      notes.push('Можно давать из чашки или бутылки');
      notes.push('При рвоте - пауза 15-30 минут');
    }

    // Дополнительные рекомендации
    notes.push('Раствор готовить строго по инструкции');
    notes.push('Хранить готовый раствор не более 24 часов в холодильнике');
    notes.push('При ухудшении состояния - обратиться к врачу');
    notes.push('Контролировать диурез (мочеиспускание)');

    return {
      type: 'rehydration',
      totalVolume: Math.round(totalVolume),
      volumeUnit: 'мл',
      hourlyRate: Math.round(hourlyRate * 10) / 10,
      rateUnit: 'мл/час',
      duration,
      solution,
      administration,
      notes,
      ageGroup
    };
  };

  const calculateDose = () => {
    setError('');
    setResult(null);
    
    // Простая валидация веса
    if (!weight) {
      setError('Пожалуйста, введите вес ребенка');
      return;
    }

    const weightNum = parseFloat(weight);
    if (isNaN(weightNum) || weightNum <= 0) {
      setError('Введите корректный вес ребенка');
      return;
    }

    if (weightNum < 1 || weightNum > 100) {
      setError('Вес должен быть в диапазоне от 1 до 100 кг');
      return;
    }

    // Если выбран расчет регидратации
    if (calculationType === 'rehydration') {
      const rehydrationResult = calculateRehydration();
      setResult(rehydrationResult);
      setActiveStep(2); // Переходим сразу к результату
      
      // Логирование действия
      if (user) {
        auditLogger.logCalculation(
          user.id,
          'rehydration_calculation',
          { age: parseFloat(age), weight: parseFloat(weight) },
          rehydrationResult,
          true
        );
      }
      
      return;
    }


    // Для расчета лекарств проверяем препарат
    if (!medication) {
      setError('Пожалуйста, выберите препарат');
      return;
    }

    setActiveStep(2); // Переходим к результату

    const ageNum = parseFloat(age) || 0;

    // Проверка соответствия веса возрасту
    const expectedWeightRanges = [
      { ageMin: 0, ageMax: 0.25, weightMin: 2.5, weightMax: 4.5 },    // новорожденные
      { ageMin: 0.25, ageMax: 0.5, weightMin: 4, weightMax: 6 },      // 1-6 месяцев
      { ageMin: 0.5, ageMax: 1, weightMin: 6, weightMax: 10 },        // 6-12 месяцев
      { ageMin: 1, ageMax: 2, weightMin: 9, weightMax: 13 },          // 1-2 года
      { ageMin: 2, ageMax: 3, weightMin: 12, weightMax: 16 },         // 2-3 года
      { ageMin: 3, ageMax: 5, weightMin: 14, weightMax: 20 },         // 3-5 лет
      { ageMin: 5, ageMax: 7, weightMin: 18, weightMax: 25 },         // 5-7 лет
      { ageMin: 7, ageMax: 10, weightMin: 22, weightMax: 35 },        // 7-10 лет
      { ageMin: 10, ageMax: 12, weightMin: 28, weightMax: 45 },       // 10-12 лет
      { ageMin: 12, ageMax: 15, weightMin: 35, weightMax: 60 },       // 12-15 лет
      { ageMin: 15, ageMax: 18, weightMin: 45, weightMax: 80 },       // 15-18 лет
    ];

    const ageRange = expectedWeightRanges.find(range => 
      ageNum >= range.ageMin && ageNum < range.ageMax
    );

    if (ageRange && (weightNum < ageRange.weightMin || weightNum > ageRange.weightMax)) {
      setError(`Вес ${weightNum} кг не соответствует возрасту ${ageNum} лет. Ожидаемый диапазон: ${ageRange.weightMin}-${ageRange.weightMax} кг. Проверьте правильность ввода данных.`);
      setActiveStep(0);
      return;
    }

    // Определение возрастной группы
    let ageGroup = '';
    if (ageNum < 1) ageGroup = 'Грудной возраст (до 1 года)';
    else if (ageNum < 3) ageGroup = 'Ранний возраст (1-3 года)';
    else if (ageNum < 7) ageGroup = 'Дошкольный возраст (3-7 лет)';
    else if (ageNum < 12) ageGroup = 'Младший школьный возраст (7-12 лет)';
    else ageGroup = 'Подростковый возраст (12+ лет)';

    // Проверка возрастных ограничений
    const selectedMed = medications.find(m => m.value === medication);
    if (selectedMed && (ageNum < selectedMed.minAge || ageNum > selectedMed.maxAge)) {
      setError(`Препарат ${selectedMed.label} не рекомендуется для возраста ${ageNum} лет. Возрастные ограничения: ${selectedMed.minAge}-${selectedMed.maxAge} лет`);
      setActiveStep(0);
      return;
    }

    // Расчет доз для различных препаратов
    let calculatedResult: CalculationResult;

    switch (medication) {
      case 'paracetamol':
        let dose: number;
        let frequency: string;
        let maxDailyDose: number;
        
        if (ageNum < 0.25) {
          // Новорожденные до 1 месяца
          dose = Math.round(weightNum * 10 * 10) / 10; // 10 мг/кг
          frequency = '3 раза в день';
          maxDailyDose = Math.round(weightNum * 30);
        } else if (ageNum < 1) {
          // Грудной возраст
          dose = Math.round(weightNum * 15 * 10) / 10; // 15 мг/кг
          frequency = '3-4 раза в день';
          maxDailyDose = Math.round(weightNum * 60);
        } else {
          // Дети старше 1 года
          dose = Math.round(weightNum * 15 * 10) / 10; // 15 мг/кг
          frequency = '3-4 раза в день';
          maxDailyDose = Math.round(weightNum * 60);
        }
        
        calculatedResult = {
          dose,
          unit: 'мг',
          frequency,
          duration: 'Не более 5 дней',
          maxDailyDose,
          ageGroup,
          type: 'medication',
          notes: [
            'Максимальная суточная доза: 60 мг/кг (30 мг/кг для новорожденных)',
            'Интервал между приемами: 6-8 часов',
            'Не применять при заболеваниях печени',
            'Доступен в форме сиропа, свечей, таблеток',
            'При температуре выше 38.5°C у детей до 3 месяцев - срочно к врачу'
          ]
        };
        break;

      case 'ibuprofen':
        let ibuprofenDose: number;
        let ibuprofenFrequency: string;
        let ibuprofenMaxDailyDose: number;
        
        if (ageNum < 0.25) {
          // Не рекомендуется до 3 месяцев
          setError('Ибупрофен не рекомендуется детям до 3 месяцев');
          setActiveStep(0);
          return;
        } else if (ageNum < 1) {
          // Грудной возраст
          ibuprofenDose = Math.round(weightNum * 5 * 10) / 10; // 5 мг/кг
          ibuprofenFrequency = '3 раза в день';
          ibuprofenMaxDailyDose = Math.round(weightNum * 20);
        } else {
          // Дети старше 1 года
          ibuprofenDose = Math.round(weightNum * 10 * 10) / 10; // 10 мг/кг
          ibuprofenFrequency = '3 раза в день';
          ibuprofenMaxDailyDose = Math.round(weightNum * 30);
        }
        
        calculatedResult = {
          dose: ibuprofenDose,
          unit: 'мг',
          frequency: ibuprofenFrequency,
          duration: 'Не более 7 дней',
          maxDailyDose: ibuprofenMaxDailyDose,
          ageGroup,
          type: 'medication',
          notes: [
            'Максимальная суточная доза: 30 мг/кг (20 мг/кг для детей до 1 года)',
            'Принимать во время еды',
            'Противопоказан при язвенной болезни',
            'Не применять у детей до 3 месяцев',
            'Может вызывать желудочно-кишечные расстройства'
          ]
        };
        break;

      case 'cetirizine':
        let cetirizineDose: number;
        let cetirizineFrequency: string;
        
        if (ageNum < 2) {
          cetirizineDose = 0.25; // 2.5 мг/мл * 0.1 мл/кг
          cetirizineFrequency = '1 раз в день';
        } else if (ageNum < 6) {
          cetirizineDose = 2.5;
          cetirizineFrequency = '1 раз в день';
        } else if (ageNum < 12) {
          cetirizineDose = 5;
          cetirizineFrequency = '1 раз в день';
        } else {
          cetirizineDose = 10;
          cetirizineFrequency = '1 раз в день';
        }
        
        calculatedResult = {
          dose: cetirizineDose,
          unit: 'мг',
          frequency: cetirizineFrequency,
          duration: 'По показаниям',
          maxDailyDose: ageNum < 6 ? 5 : 10,
          ageGroup,
          type: 'medication',
          notes: [
            'Принимать вечером перед сном',
            'Может вызывать сонливость',
            'Не применять у детей до 6 месяцев',
            'Доступен в форме капель и таблеток'
          ]
        };
        break;

      case 'levocetirizine':
        let levocetirizineDose: number;
        let levocetirizineFrequency: string;
          
        if (ageNum < 2) {
          levocetirizineDose = 0.125; // 2.5 мг/мл * 0.05 мл/кг
          levocetirizineFrequency = '1 раз в день';
        } else if (ageNum < 6) {
          levocetirizineDose = 1.25;
          levocetirizineFrequency = '1 раз в день';
        } else if (ageNum < 12) {
          levocetirizineDose = 2.5;
          levocetirizineFrequency = '1 раз в день';
        } else {
          levocetirizineDose = 5;
          levocetirizineFrequency = '1 раз в день';
        }
        
        calculatedResult = {
          dose: levocetirizineDose,
          unit: 'мг',
          frequency: levocetirizineFrequency,
          duration: 'По показаниям',
          maxDailyDose: ageNum < 6 ? 2.5 : 5,
          ageGroup,
          type: 'medication',
          notes: [
            'Принимать вечером перед сном',
            'Меньше седативного эффекта чем цетиризин',
            'Не применять у детей до 6 месяцев',
            'Доступен в форме капель и таблеток'
          ]
        };
        break;

      case 'amoxicillin':
        const amoxicillinDose = Math.round(weightNum * 45 * 10) / 10;
        calculatedResult = {
          dose: amoxicillinDose,
          unit: 'мг',
          frequency: '3 раза в день',
          duration: '7-10 дней',
          maxDailyDose: Math.min(Math.round(weightNum * 135), 1000),
          ageGroup,
          type: 'medication',
          notes: [
            'Максимальная суточная доза: 1000 мг',
            'Принимать независимо от приема пищи',
            'Возможны аллергические реакции',
            'Доступен в форме суспензии и таблеток'
          ]
        };
        break;

      case 'clarithromycin':
        const clarithromycinDose = Math.round(weightNum * 15 * 10) / 10;
        calculatedResult = {
          dose: clarithromycinDose,
          unit: 'мг',
          frequency: '2 раза в день',
          duration: '7-10 дней',
          maxDailyDose: Math.min(Math.round(weightNum * 30), 1000),
          ageGroup,
          type: 'medication',
          notes: [
            'Максимальная суточная доза: 1000 мг',
            'Принимать во время еды',
            'Возможны желудочно-кишечные расстройства',
            'Доступен в форме суспензии и таблеток',
            'Не применять при аллергии на макролиды'
          ]
        };
        break;

      case 'cefuroxime':
        const cefuroximeDose = Math.round(weightNum * 30 * 10) / 10;
        calculatedResult = {
          dose: cefuroximeDose,
          unit: 'мг',
          frequency: '2 раза в день',
          duration: '7-10 дней',
          maxDailyDose: Math.min(Math.round(weightNum * 60), 1000),
          ageGroup,
          type: 'medication',
          notes: [
            'Максимальная суточная доза: 1000 мг',
            'Принимать во время еды',
            'Возможны аллергические реакции',
            'Доступен в форме суспензии и таблеток',
            'Не применять при аллергии на цефалоспорины'
          ]
        };
        break;

      case 'cefixime':
        const cefiximeDose = Math.round(weightNum * 8 * 10) / 10;
        calculatedResult = {
          dose: cefiximeDose,
          unit: 'мг',
          frequency: '1 раз в день',
          duration: '7-10 дней',
          maxDailyDose: Math.min(Math.round(weightNum * 16), 400),
          ageGroup,
          type: 'medication',
          notes: [
            'Максимальная суточная доза: 400 мг',
            'Принимать независимо от приема пищи',
            'Возможны аллергические реакции',
            'Доступен в форме суспензии и таблеток',
            'Не применять при аллергии на цефалоспорины'
          ]
        };
        break;

      case 'azithromycin':
        const azithromycinDose = Math.round(weightNum * 10 * 10) / 10;
        calculatedResult = {
          dose: azithromycinDose,
          unit: 'мг',
          frequency: '1 раз в день',
          duration: '3-5 дней',
          maxDailyDose: Math.min(Math.round(weightNum * 20), 500),
          ageGroup,
          type: 'medication',
          notes: [
            'Принимать за 1 час до еды',
            'Может вызывать тошноту',
            'Не применять при аллергии на макролиды',
            'Доступен в форме суспензии и таблеток'
          ]
        };
        break;

      case 'salbutamol':
        const salbutamolDose = Math.round(weightNum * 0.1 * 10) / 10;
        calculatedResult = {
          dose: salbutamolDose,
          unit: 'мг',
          frequency: '3-4 раза в день',
          duration: 'По показаниям',
          maxDailyDose: 5,
          ageGroup,
          type: 'medication',
          notes: [
            'Ингаляционно через небулайзер',
            'Максимальная доза: 5 мг',
            'Может вызывать тахикардию',
            'Доступен в форме раствора для ингаляций'
          ]
        };
        break;

      case 'furosemide':
        const furosemideDose = Math.round(weightNum * 1 * 10) / 10;
        calculatedResult = {
          dose: furosemideDose,
          unit: 'мг',
          frequency: '1-2 раза в день',
          duration: 'По показаниям',
          maxDailyDose: Math.round(weightNum * 2),
          ageGroup,
          type: 'medication',
          notes: [
            'Принимать утром',
            'Контролировать электролиты',
            'Может вызывать гипокалиемию',
            'Доступен в форме таблеток и раствора для инъекций'
          ]
        };
        break;

      case 'omeprazole':
        let omeprazoleDose: number;
        let omeprazoleFrequency: string;
        
        if (ageNum < 1) {
          setError('Омепразол не рекомендуется детям до 1 года');
          setActiveStep(0);
          return;
        } else if (ageNum < 12) {
          omeprazoleDose = Math.round(weightNum * 0.5 * 10) / 10; // 0.5 мг/кг
          omeprazoleFrequency = '1 раз в день';
        } else {
          omeprazoleDose = 20; // 20 мг для подростков
          omeprazoleFrequency = '1 раз в день';
        }
        
        calculatedResult = {
          dose: omeprazoleDose,
          unit: 'мг',
          frequency: omeprazoleFrequency,
          duration: '2-4 недели',
          maxDailyDose: ageNum < 12 ? Math.round(weightNum * 1) : 40,
          ageGroup,
          type: 'medication',
          notes: [
            'Принимать утром натощак',
            'Не разжевывать капсулы',
            'Может вызывать головную боль',
            'Доступен в форме капсул'
          ]
        };
        break;

      case 'loperamide':
        let loperamideDose: number;
        let loperamideFrequency: string;
        
        if (ageNum < 2) {
          setError('Лоперамид не рекомендуется детям до 2 лет');
          setActiveStep(0);
          return;
        } else if (ageNum < 5) {
          loperamideDose = 1; // 1 мг
          loperamideFrequency = '2 раза в день';
        } else if (ageNum < 8) {
          loperamideDose = 2; // 2 мг
          loperamideFrequency = '2 раза в день';
        } else {
          loperamideDose = 2; // 2 мг
          loperamideFrequency = '2-3 раза в день';
        }
        
        calculatedResult = {
          dose: loperamideDose,
          unit: 'мг',
          frequency: loperamideFrequency,
          duration: 'Не более 2 дней',
          maxDailyDose: ageNum < 8 ? 4 : 6,
          ageGroup,
          type: 'medication',
          notes: [
            'Принимать после каждого жидкого стула',
            'Не применять при инфекционной диарее',
            'Может вызывать запор',
            'Доступен в форме капсул и таблеток'
          ]
        };
        break;

      case 'domperidone':
        let domperidoneDose: number;
        let domperidoneFrequency: string;
        
        if (ageNum < 1) {
          domperidoneDose = Math.round(weightNum * 0.25 * 10) / 10; // 0.25 мг/кг
          domperidoneFrequency = '3 раза в день';
        } else if (ageNum < 12) {
          domperidoneDose = Math.round(weightNum * 0.5 * 10) / 10; // 0.5 мг/кг
          domperidoneFrequency = '3 раза в день';
        } else {
          domperidoneDose = 10; // 10 мг для подростков
          domperidoneFrequency = '3 раза в день';
        }
        
        calculatedResult = {
          dose: domperidoneDose,
          unit: 'мг',
          frequency: domperidoneFrequency,
          duration: 'По показаниям',
          maxDailyDose: ageNum < 12 ? Math.round(weightNum * 1.5) : 30,
          ageGroup,
          type: 'medication',
          notes: [
            'Принимать за 15-30 минут до еды',
            'Может вызывать сонливость',
            'Не применять при кишечной непроходимости',
            'Доступен в форме таблеток и суспензии'
          ]
        };
        break;

      case 'diphenhydramine':
        let diphenhydramineDose: number;
        let diphenhydramineFrequency: string;
        
        if (ageNum < 1) {
          setError('Дифенгидрамин не рекомендуется детям до 1 года');
          setActiveStep(0);
          return;
        } else if (ageNum < 6) {
          diphenhydramineDose = Math.round(weightNum * 0.5 * 10) / 10; // 0.5 мг/кг
          diphenhydramineFrequency = '3-4 раза в день';
        } else if (ageNum < 12) {
          diphenhydramineDose = 12.5; // 12.5 мг
          diphenhydramineFrequency = '3-4 раза в день';
        } else {
          diphenhydramineDose = 25; // 25 мг
          diphenhydramineFrequency = '3-4 раза в день';
        }
        
        calculatedResult = {
          dose: diphenhydramineDose,
          unit: 'мг',
          frequency: diphenhydramineFrequency,
          duration: 'По показаниям',
          maxDailyDose: ageNum < 6 ? Math.round(weightNum * 2) : 100,
          ageGroup,
          type: 'medication',
          notes: [
            'Вызывает выраженную сонливость',
            'Не применять при бронхиальной астме',
            'Может вызывать сухость во рту',
            'Доступен в форме таблеток и сиропа'
          ]
        };
        break;

      default:
        setError('Выбранный препарат не поддерживается');
        setActiveStep(0);
        return;
    }

    // Добавление предупреждений о безопасности
    const safetyWarnings: string[] = [];
    
    // Проверка критических доз
    if (medication === 'paracetamol' && calculatedResult.dose > weightNum * 20) {
      safetyWarnings.push('⚠️ ВНИМАНИЕ: Доза парацетамола превышает 20 мг/кг - высокий риск гепатотоксичности!');
    }
    
    if (medication === 'ibuprofen' && calculatedResult.dose > weightNum * 15) {
      safetyWarnings.push('⚠️ ВНИМАНИЕ: Доза ибупрофена превышает 15 мг/кг - высокий риск побочных эффектов!');
    }
    
    // Проверка возраста
    if (ageNum < 0.25 && (medication === 'ibuprofen' || medication === 'cetirizine' || medication === 'levocetirizine')) {
      safetyWarnings.push('🚨 КРИТИЧНО: Препарат не рекомендуется для новорожденных!');
    }
    
    // Проверка веса
    if (weightNum < 5 && medication === 'loperamide') {
      safetyWarnings.push('🚨 КРИТИЧНО: Лоперамид противопоказан детям с весом менее 5 кг!');
    }
    
    // Добавление предупреждений к заметкам
    if (safetyWarnings.length > 0) {
      calculatedResult.notes = [...safetyWarnings, ...calculatedResult.notes];
    }

    // Расширенная валидация после расчета дозировки
    const finalMedicalData = {
      age: parseFloat(age),
      weight: parseFloat(weight),
      medication: medication,
      dosage: calculatedResult.dose
    };
    
    const advancedValidation = dataValidationManager.validateData(finalMedicalData, 'calculation');
    
    // Логирование попытки расчета
    auditLogger.logCalculation(
      user?.id || 'guest',
      'dosage_calculation',
      finalMedicalData,
      calculatedResult,
      advancedValidation.results.isValid
    );

    setResult(calculatedResult);

    // Сохранение в историю
    if (user) {
      const selectedMedication = medications.find(m => m.value === medication);
      if (selectedMedication) {
        historyManager.addHistoryItem({
          userId: user.id,
          action: 'calculation',
          title: `Расчет дозы ${selectedMedication.label}`,
          description: `Возраст: ${age} лет, Вес: ${weight} кг`,
          data: {
            medication: selectedMedication.value,
            age: parseFloat(age),
            weight: parseFloat(weight),
            result: calculatedResult
          },
          section: 'Калькулятор доз',
          isFavorite: false
        });

        // Добавление в избранное (если пользователь часто использует этот расчет)
        const favoriteData = {
          medication: selectedMedication.value,
          age: parseFloat(age),
          weight: parseFloat(weight),
          result: calculatedResult
        };

        if (!favoritesManager.isFavorite(user.id, 'calculation', favoriteData)) {
          favoritesManager.addToFavorites({
            userId: user.id,
            type: 'calculation',
            title: `${selectedMedication.label}`,
            description: `Возраст: ${age} лет, Вес: ${weight} кг`,
            data: favoriteData,
            category: 'Расчеты доз',
            tags: [selectedMedication.category]
          });
        }
      }
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const resetForm = () => {
    setWeight('');
    setAge('');
    setMedication('');
    setCalculationType('medication');
    setResult(null);
    setError('');
    setActiveStep(0);
  };

  const medicationInfo = [
    {
      name: 'Парацетамол',
      category: 'Жаропонижающие',
      indications: ['Лихорадка', 'Боль'],
      contraindications: ['Заболевания печени'],
      sideEffects: ['Аллергические реакции']
    },
    {
      name: 'Ибупрофен',
      category: 'НПВС',
      indications: ['Лихорадка', 'Боль', 'Воспаление'],
      contraindications: ['Язвенная болезнь'],
      sideEffects: ['Тошнота', 'Головная боль']
    },
    {
      name: 'Амоксициллин',
      category: 'Антибиотики',
      indications: ['Бактериальные инфекции'],
      contraindications: ['Аллергия на пенициллины'],
      sideEffects: ['Диарея', 'Аллергические реакции']
    },
    {
      name: 'Цетиризин',
      category: 'Антигистаминные',
      indications: ['Аллергические реакции'],
      contraindications: ['Аллергия на цетиризин'],
      sideEffects: ['Сонливость', 'Сухость во рту']
    },
    {
      name: 'Сальбутамол',
      category: 'Бронхолитики',
      indications: ['Бронхоспазм'],
      contraindications: ['Аллергия на сальбутамол'],
      sideEffects: ['Тахикардия', 'Тремор']
    },
    {
      name: 'Фуросемид',
      category: 'Диуретики',
      indications: ['Отеки'],
      contraindications: ['Анурия'],
      sideEffects: ['Гипокалиемия', 'Гипотония']
    }
  ];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h1" sx={{ 
            mb: 2, 
            background: 'linear-gradient(45deg, #4A90E2, #50C878)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700
          }}>
            Калькулятор доз
          </Typography>
          <Typography variant="body1" sx={{ color: '#718096', maxWidth: 800, fontSize: '1.1rem' }}>
            Точный расчет дозировок лекарственных препаратов для детей с учетом веса, 
            возраста и показаний к применению. Включает современные антигистаминные препараты.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={resetForm}
          sx={{ 
            ml: 3,
            borderRadius: 2,
            borderColor: '#4A90E2',
            color: '#4A90E2',
            '&:hover': {
              borderColor: '#357ABD',
              backgroundColor: 'rgba(74, 144, 226, 0.04)'
            }
          }}
        >
          Новый расчет
        </Button>
      </Box>

      <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
        <Typography variant="body2">
          <strong>Внимание:</strong> Данный калькулятор предназначен только для справочных целей. 
          Все назначения должны быть согласованы с лечащим врачом. 
          Дозировки могут варьироваться в зависимости от индивидуальных особенностей пациента.
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 3 }}>
        <Box sx={{ flex: '1 1 400px' }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <MedicationIcon sx={{ color: '#4A90E2', mr: 1, fontSize: 28 }} />
                <Typography variant="h3" sx={{ fontWeight: 600 }}>
                  Параметры расчета
                </Typography>
              </Box>

              <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel>{isMobile ? '' : label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Fade in={activeStep === 0}>
                <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Тип расчета *</InputLabel>
                    <Select
                      value={calculationType}
                      label="Тип расчета *"
                      onChange={(e) => setCalculationType(e.target.value as 'medication' | 'rehydration')}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="medication">
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <MedicationIcon sx={{ mr: 1, color: '#4A90E2' }} />
                          <Typography variant="body1" sx={{ flex: 1 }}>
                            Расчет дозы лекарств
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="rehydration">
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <HospitalIcon sx={{ mr: 1, color: '#4A90E2' }} />
                          <Typography variant="body1" sx={{ flex: 1 }}>
                            Регидратационная терапия
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mb: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        label="Вес ребенка (кг) *"
                        type="number"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        inputProps={{ min: 1, max: 100, step: 0.1 }}
                        InputProps={{
                          startAdornment: <ScaleIcon sx={{ color: '#4A90E2', mr: 1 }} />
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        label="Возраст (лет)"
                        type="number"
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                        inputProps={{ min: 0, max: 18, step: 0.5 }}
                        InputProps={{
                          startAdornment: <ScheduleIcon sx={{ color: '#4A90E2', mr: 1 }} />
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Box>
                  </Box>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={calculationType === 'rehydration' ? calculateDose : handleNext}
                    sx={{ 
                      borderRadius: 2, 
                      py: 1.5,
                      background: 'linear-gradient(45deg, #4A90E2, #50C878)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #357ABD, #45B06A)',
                      }
                    }}
                  >
                    {calculationType === 'rehydration' ? 'Рассчитать' : 'Далее'}
                  </Button>
                </Box>
              </Fade>

              <Fade in={activeStep === 1}>
                <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
                  {calculationType === 'medication' ? (
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Препарат *</InputLabel>
                      <Select
                        value={medication}
                        label="Препарат *"
                        onChange={(e) => setMedication(e.target.value)}
                        sx={{ borderRadius: 2 }}
                      >
                        {medications.map((med) => (
                          <MenuItem key={med.value} value={med.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <Typography variant="body1" sx={{ flex: 1 }}>
                                {med.label}
                              </Typography>
                              <Chip 
                                label={med.category} 
                                size="small" 
                                sx={{ 
                                  ml: 1, 
                                  backgroundColor: '#E8F5E8', 
                                  color: '#50C878',
                                  fontSize: '0.7rem'
                                }} 
                              />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <HospitalIcon sx={{ fontSize: 64, color: '#4A90E2', mb: 2 }} />
                      <Typography variant="h5" sx={{ mb: 2, color: '#4A90E2' }}>
                        Регидратационная терапия
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
                        Расчет пероральной регидратации по возрасту и весу ребенка
                      </Typography>
                    </Box>
                  )}
                  
                  {!result && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleBack}
                        sx={{ flex: 1, borderRadius: 2 }}
                      >
                        Назад
                      </Button>
                      <Button
                        variant="contained"
                        onClick={calculationType === 'medication' ? calculateDose : handleNext}
                        disabled={calculationType === 'medication' && !medication}
                        sx={{ 
                          flex: 1, 
                          borderRadius: 2,
                          background: 'linear-gradient(45deg, #4A90E2, #50C878)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #357ABD, #45B06A)',
                          }
                        }}
                      >
                        {calculationType === 'medication' ? 'Рассчитать' : 'Далее'}
                      </Button>
                    </Box>
                  )}
                  
                  {result && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={resetForm}
                        sx={{ flex: 1, borderRadius: 2 }}
                      >
                        Новый расчет
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setResult(null);
                          setError('');
                          setActiveStep(0);
                        }}
                        sx={{ 
                          flex: 1, 
                          borderRadius: 2,
                          background: 'linear-gradient(45deg, #4A90E2, #50C878)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #357ABD, #45B06A)',
                          }
                        }}
                      >
                        Изменить параметры
                      </Button>
                    </Box>
                  )}
                </Box>
              </Fade>


              {error && (
                <Slide direction="down" in={!!error}>
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {error}
                  </Alert>
                </Slide>
              )}

              {result && (
                <Fade in={!!result}>
                  <Box sx={{ mt: 3 }}>
                    {result.type === 'rehydration' ? (
                      // Отображение результата регидратации
                      <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <HospitalIcon sx={{ color: '#4A90E2', mr: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            Регидратационная терапия
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                          <Typography variant="h1" sx={{ 
                            color: '#4A90E2', 
                            fontWeight: 700,
                            background: 'linear-gradient(45deg, #4A90E2, #50C878)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                          }}>
                            {(result as RehydrationResult).totalVolume} {(result as RehydrationResult).volumeUnit}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                            {(result as RehydrationResult).ageGroup}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#4A90E2' }}>
                              Скорость введения:
                            </Typography>
                            <Typography variant="body1">
                              {(result as RehydrationResult).hourlyRate} {(result as RehydrationResult).rateUnit}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#4A90E2' }}>
                              Длительность:
                            </Typography>
                            <Typography variant="body1">
                              {(result as RehydrationResult).duration}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#F0F8FF', borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#4A90E2', mb: 1 }}>
                            Раствор: {(result as RehydrationResult).solution}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#4A90E2' }}>
                            Способ введения: {(result as RehydrationResult).administration}
                          </Typography>
                        </Box>
                      </Alert>
                    ) : (
                      // Отображение результата лекарств
                      <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <MedicationIcon sx={{ color: '#4A90E2', mr: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            Результат расчета
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                          <Typography variant="h1" sx={{ 
                            color: '#4A90E2', 
                            fontWeight: 700,
                            background: 'linear-gradient(45deg, #4A90E2, #50C878)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                          }}>
                            {result.dose} {result.unit}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                            {result.ageGroup}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#4A90E2' }}>
                              Частота приема:
                            </Typography>
                            <Typography variant="body1">
                              {result.frequency}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#4A90E2' }}>
                              Длительность:
                            </Typography>
                            <Typography variant="body1">
                              {result.duration}
                            </Typography>
                          </Box>
                        </Box>
                        {result.maxDailyDose && (
                          <Box sx={{ mt: 2, p: 2, backgroundColor: '#F0F8FF', borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#4A90E2' }}>
                              Максимальная суточная доза: {result.maxDailyDose} {result.unit}
                            </Typography>
                          </Box>
                        )}
                      </Alert>
                    )}

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h4" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                        Важные замечания:
                      </Typography>
                      <List dense>
                        {result.notes.map((note, index) => (
                          <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemIcon>
                              <InfoIcon sx={{ color: '#4A90E2', fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={note} 
                              sx={{ '& .MuiListItemText-primary': { fontSize: '0.95rem' } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                </Fade>
              )}
              
              {/* Медицинская информация и валидация */}
              {validationResult && (
                <Box sx={{ mt: 3 }}>
                  <MedicalInfo 
                    validationResult={validationResult}
                    sourceReferences={[
                      sourceManager.createReference('pediatric-formulary', 'Дозировки', 'Педиатрические расчеты')
                    ]}
                    protocolId="dosage-protocol"
                    showDetails={true}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 400px' }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: '#4A90E2' }}>
                Справочная информация
              </Typography>

              {medicationInfo.map((med, index) => (
                <Accordion key={index} sx={{ mb: 1, borderRadius: 2, '&:before': { display: 'none' } }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      backgroundColor: '#F8F9FA', 
                      borderRadius: 2,
                      '&.Mui-expanded': { 
                        backgroundColor: '#E8F5E8',
                        borderRadius: '8px 8px 0 0'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography variant="body1" sx={{ flex: 1, fontWeight: 600 }}>
                        {med.name}
                      </Typography>
                      <Chip 
                        label={med.category} 
                        size="small" 
                        sx={{ 
                          backgroundColor: '#E8F5E8', 
                          color: '#50C878',
                          fontSize: '0.7rem'
                        }} 
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: '#FAFAFA', borderRadius: '0 0 8px 8px', py: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4A90E2', mb: 0.5, fontSize: '0.8rem' }}>
                          Показания:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {med.indications.join(', ')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#FF6B6B', mb: 0.5, fontSize: '0.8rem' }}>
                          Противопоказания:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {med.contraindications.join(', ')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#FF9800', mb: 0.5, fontSize: '0.8rem' }}>
                          Побочные эффекты:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {med.sideEffects.join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="body1" sx={{ mb: 1.5, color: '#4A90E2', fontWeight: 600, fontSize: '0.9rem' }}>
                  Принципы дозирования:
                </Typography>
                <List dense sx={{ py: 0 }}>
                  <ListItem sx={{ py: 0.25, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <CheckCircleIcon sx={{ color: '#50C878', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Расчет на кг массы тела" 
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.25, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <CheckCircleIcon sx={{ color: '#50C878', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Учет возраста и состояния органов" 
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.25, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <CheckCircleIcon sx={{ color: '#50C878', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Контроль эффективности и безопасности" 
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                    />
                  </ListItem>
                </List>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default Calculator;
